import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Box,
  InputLabel,
  MenuItem,
  FormControl,
  FormControlLabel,
  Select,
  Switch,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { convertBlobToBase64 } from '../lib/network/helpers';
import { setSelectedNode } from '../features/networkSlice';
import { updateNode } from '../features/networkUndoSlice';
import { sameId, SHAPE_SIZES } from '../lib/model';
import {
  DISPLAY_ID, DISPLAY_LABEL, SHAPES_FOR_MODE, presentShape, isIdMode,
} from '../lib/display';
import ImageTabs from './EditNodeDialog/ImageTabs';
import ColorField from './common/ColorField';
import { COLORS } from '../lib/network/constants';

export default function EditNodeDialog() {
  const dispatch = useDispatch();
  const selectedNode = useSelector((state) => state.network.selectedNode);
  const nodes = useSelector((state) => state.networkUndo.present.nodes);
  const displayMode = useSelector((state) => (
    state.network.displayMode === DISPLAY_ID ? DISPLAY_ID : DISPLAY_LABEL
  ));
  const shapeOptions = SHAPES_FOR_MODE[displayMode];

  const [formData, setFormData] = useState({});
  const [idError, setIdError] = useState(null);

  useEffect(() => {
    if (selectedNode) {
      const initial = { ...selectedNode };
      const drawn = presentShape(initial.shape, DISPLAY_LABEL);
      if (['ellipse', 'box'].includes(drawn) && initial.resized !== true) {
        initial.width = SHAPE_SIZES[drawn].width;
        initial.height = SHAPE_SIZES[drawn].height;
      }
      setFormData(initial);
    }
  }, [selectedNode]);

  const handleClose = () => {
    setIdError(null);
    dispatch((setSelectedNode(null)));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === 'id') { // make 'id' alphanumeric
      newValue = newValue.replace(/[^a-zA-Z0-9\-_:]/g, '');
    }
    if (name === 'physics') {
      newValue = e.target.checked;
    }
    if (name === 'backgroundColor' || name === 'borderColor') {
      const current = typeof formData.color === 'object' && formData.color ? formData.color : {};
      const newColor = {
        background: name === 'backgroundColor' ? value : (current.background ?? ''),
        border: name === 'borderColor' ? value : (current.border ?? ''),
      };
      setFormData({
        ...formData,
        color: (newColor.background || newColor.border) ? newColor : null,
      });
      setIdError(null);
      return;
    }
    if (name === 'shape') {
      const next = { ...formData, shape: newValue };
      if (next.resized !== true) {
        const defaults = SHAPE_SIZES[newValue];
        if (defaults && (newValue === 'ellipse' || newValue === 'box')) {
          next.width = defaults.width;
          next.height = defaults.height;
        } else {
          delete next.width;
          delete next.height;
        }
      }
      setFormData(next);
      setIdError(null);
      return;
    }
    if (name === 'width' || name === 'height') {
      if (value === '' || value === null || value === undefined) {
        newValue = '';
      } else {
        const parsed = parseInt(value, 10);
        newValue = Number.isNaN(parsed) ? '' : parsed;
      }
      setFormData({ ...formData, [name]: newValue, resized: true });
      setIdError(null);
      return;
    }
    setFormData({ ...formData, [name]: newValue });
    setIdError(null);
  };

  const handleSave = async () => {
    const updatedNode = { ...formData };

    // empty sizes, or a shape change without a resize, fall back to the shape defaults
    if (updatedNode.width === '' || updatedNode.width === null) delete updatedNode.width;
    if (updatedNode.height === '' || updatedNode.height === null) delete updatedNode.height;
    if (updatedNode.resized !== true) {
      updatedNode.width = null;
      updatedNode.height = null;
      delete updatedNode.resized;
    }

    // only keep colours the user actually set
    if (updatedNode.color && typeof updatedNode.color === 'object') {
      const color = {};
      if (updatedNode.color.background) color.background = updatedNode.color.background;
      if (updatedNode.color.border) color.border = updatedNode.color.border;
      updatedNode.color = Object.keys(color).length > 0 ? color : null;
    } else if (!updatedNode.color) {
      updatedNode.color = null;
    }

    if (updatedNode.physics !== false) delete updatedNode.physics;
    if (!updatedNode.title) updatedNode.title = null;

    // ids: keep the original value unless it was actually changed
    if (sameId(selectedNode.id, updatedNode.id)) {
      updatedNode.id = selectedNode.id;
    } else {
      if (nodes.some((item) => sameId(item.id, updatedNode.id))) {
        setIdError('A node with that ID already exists');
        return;
      }
      updatedNode.oldId = selectedNode.id;
    }

    // convert local images to base64
    if (updatedNode.shape === 'image'
      && updatedNode.image
      && updatedNode.image.startsWith('blob:http')
    ) {
      updatedNode.image = await convertBlobToBase64(updatedNode.image);
    }

    dispatch(updateNode(updatedNode));
    dispatch(setSelectedNode(null));
  };

  const bgColor = formData?.color?.background || '';
  const borderColor = formData?.color?.border || '';

  return (
    <Dialog open={selectedNode !== null} onClose={handleClose} fullWidth maxWidth="md">
      {formData && (
        <form
          autoComplete="off"
          action="/"
          method="POST"
          onSubmit={(e) => { e.preventDefault(); handleSave(); }}
        >
          <DialogTitle>Edit Node</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                error={!!idError}
                helperText={idError || null}
                fullWidth
                size="small"
                value={formData.id ?? ''}
                name="id"
                label="ID"
                onChange={handleChange}
              />
              <TextField
                fullWidth
                size="small"
                value={formData.label || ''}
                name="label"
                label="Label"
                onChange={handleChange}
                autoFocus
              />
              <TextField
                fullWidth
                size="small"
                multiline
                minRows="3"
                maxRows="10"
                value={formData.title || ''}
                name="title"
                label="Title"
                onChange={handleChange}
              />
              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <InputLabel id="shape-select">Shape</InputLabel>
                <Select
                  name="shape"
                  labelId="shape-select"
                  value={presentShape(formData.shape, displayMode)}
                  label="Shape"
                  onChange={handleChange}
                >
                  {shapeOptions.map((option) => (
                    <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!isIdMode(displayMode) && ['ellipse', 'box'].includes(presentShape(formData.shape, displayMode)) && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    size="small"
                    type="number"
                    label="Width"
                    value={formData.width !== undefined && formData.width !== null && formData.width !== '' ? formData.width : ''}
                    name="width"
                    onChange={handleChange}
                    inputProps={{ min: 50, max: 300 }}
                    sx={{ width: 100 }}
                    placeholder={String(SHAPE_SIZES[presentShape(formData.shape, displayMode)].width)}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Height"
                    value={formData.height !== undefined && formData.height !== null && formData.height !== '' ? formData.height : ''}
                    name="height"
                    onChange={handleChange}
                    inputProps={{ min: 30, max: 200 }}
                    sx={{ width: 100 }}
                    placeholder={String(SHAPE_SIZES[presentShape(formData.shape, displayMode)].height)}
                  />
                </Box>
              )}
              {!isIdMode(displayMode) && formData.shape === 'image' && (
                <ImageTabs formData={formData} setFormData={setFormData} />
              )}
              <FormControlLabel
                control={<Switch />}
                name="physics"
                label="Physics"
                title="Let auto layout move this node"
                checked={formData.physics !== false}
                onChange={handleChange}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <ColorField
                  label="Fill"
                  name="backgroundColor"
                  value={bgColor}
                  fallback={COLORS.nodeFill}
                  onChange={handleChange}
                />
                <ColorField
                  label="Border"
                  name="borderColor"
                  value={borderColor}
                  fallback={COLORS.nodeStroke}
                  onChange={handleChange}
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" type="submit">Save</Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}
